import { useState } from 'react';
import { Icon } from '../Icon';
import { TASK_CATEGORIES } from '../../lib/taskProcess';
import type { NewTaskPayload } from '../../hooks/useAppStore';
import type { Member, SessionUser } from '../../types';

interface RegisterTaskModalProps {
    departmentMembers: Member[];
    sessionUser: SessionUser;
    onClose: () => void;
    onSubmit: (payload: NewTaskPayload) => void;
}

export function RegisterTaskModal({ departmentMembers, sessionUser, onClose, onSubmit }: RegisterTaskModalProps) {
    const [newTitle, setNewTitle] = useState('');
    const [newType, setNewType] = useState('QUANTITATIVE');
    const [newTaskCategory, setNewTaskCategory] = useState('CORE');
    const [newPeriod, setNewPeriod] = useState('');
    const [newTarget, setNewTarget] = useState('');
    const [newCoworkerIds, setNewCoworkerIds] = useState<string[]>([]);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
                >
                    <Icon name="x" className="w-5 h-5" />
                </button>
                <h3 className="text-lg font-bold text-slate-900 mb-4">신규 중점 과제 등록</h3>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    onSubmit({
                        title: newTitle,
                        type: newType,
                        taskCategory: newTaskCategory,
                        targetValue: newTarget,
                        period: newPeriod,
                        coworkerIds: newCoworkerIds,
                    });
                }} className="space-y-4 text-sm text-slate-800">
                    <div>
                        <label className="block text-slate-700 font-medium mb-1">과제명</label>
                        <input
                            type="text"
                            required
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            placeholder="예: 영업망 전산화 구축을 위한 시스템 요구사항 정의"
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-slate-700 font-medium mb-1">과제 유형</label>
                            <select
                                value={newType}
                                onChange={(e) => setNewType(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold cursor-pointer"
                            >
                                <option value="QUANTITATIVE">정량 업무</option>
                                <option value="QUALITATIVE">정성 업무</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-slate-700 font-medium mb-1">Category</label>
                            <select
                                value={newTaskCategory}
                                onChange={(e) => setNewTaskCategory(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold cursor-pointer"
                            >
                                {TASK_CATEGORIES.map(c => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-slate-700 font-medium mb-1">수행 기한</label>
                        <input
                            type="date"
                            required
                            value={newPeriod}
                            onChange={(e) => setNewPeriod(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold"
                        />
                    </div>

                    <div>
                        <label className="block text-slate-700 font-medium mb-1">목표 및 기대수준</label>
                        <textarea
                            required
                            value={newTarget}
                            onChange={(e) => setNewTarget(e.target.value)}
                            placeholder="목표 및 퀄리티 충족 요건을 서술해주세요."
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 h-20 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold leading-relaxed"
                        ></textarea>
                    </div>

                    <div>
                        <label className="block text-slate-700 font-medium mb-1">협업 동료 (Co-worker)</label>
                        <p className="text-[11px] text-slate-400 mb-2">다면평가자 풀 추천에 사용됩니다. 동일 조직을 우선 선택하세요.</p>
                        <div className="max-h-36 overflow-y-auto border border-slate-200 rounded-lg bg-slate-50 p-2 space-y-1">
                            {departmentMembers.filter(m => m.id !== sessionUser?.id).length === 0 && (
                                <p className="text-xs text-slate-400 p-2">선택 가능한 동료가 없습니다.</p>
                            )}
                            {departmentMembers.filter(m => m.id !== sessionUser?.id).map(m => {
                                const checked = newCoworkerIds.includes(m.id);
                                return (
                                    <label key={m.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white cursor-pointer text-xs">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => {
                                                setNewCoworkerIds(prev =>
                                                    checked ? prev.filter(id => id !== m.id) : [...prev, m.id]
                                                );
                                            }}
                                            className="rounded text-indigo-600 h-3.5 w-3.5"
                                        />
                                        <span className="font-semibold text-slate-800">{m.name}</span>
                                        <span className="text-slate-400">{m.duty} · {m.department}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <div className="pt-4 flex gap-3 justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-4 py-2 rounded-lg font-bold"
                        >
                            취소
                        </button>
                        <button
                            type="submit"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-bold shadow"
                        >
                            과제 제출
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
